import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BedDouble, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { useRequireAuth } from "@/lib/useRequireAuth";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { resolveImage } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { rupees, businessDay, addDays as addBusinessDays } from "@/lib/format";

const addDays = (days) => addBusinessDays(businessDay(), days);

export function BookingSection({ business }) {
  const { t, lang, auth } = useApp();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const [units, setUnits] = useState([]);
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState({ start_on: addDays(1), end_on: addDays(2) });
  const [availability, setAvailability] = useState(null);
  const [form, setForm] = useState({ guests: 2, guest_name: "", guest_phone: "", notes: "", payment_method: "cash" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${business.id}/bookable-units`).then((r) => {
      setUnits(rows(r.data));
      if (r.data[0]) setSelected(r.data[0]);
    });
  }, [business.id]);

  const check = async () => {
    setAvailability(null);
    try {
      const { data } = await api.get(`/bookable-units/${selected.id}/availability`, { params: range });
      setAvailability(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const book = async () => {
    // One up-front convention (L2): the shared sign-in prompt, which returns here after login.
    if (!requireAuth()) return;
    setBusy(true);
    try {
      await api.post("/bookings", { unit_id: selected.id, ...range, ...form, guests: Number(form.guests) || 1 });
      if (form.payment_method === "upi") {
        navigate("/activity");
        return;
      }
      toast.success(t("bookings.booked"));
      setAvailability(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="booking-section">
      <div className="grid gap-3 sm:grid-cols-2">
        {units.map((u) => (
          <button
            key={u.id}
            onClick={() => {
              setSelected(u);
              setAvailability(null);
            }}
            data-testid={`unit-card-${u.id}`}
            className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors duration-200 ${
              selected?.id === u.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-border"
            }`}
          >
            {u.image_url ? (
              <img src={resolveImage(u.image_url)} alt={u.name} width={64} height={56} loading="lazy" decoding="async" className="h-14 w-16 rounded-xl object-cover" />
            ) : (
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-muted text-[hsl(var(--primary))]">
                {u.unit_type === "room" ? <BedDouble size={17} /> : <PartyPopper size={17} />}
              </span>
            )}
            <span>
              <span className="block font-medium">{localized(lang, u, "name")}</span>
              <span className="block text-xs text-muted-foreground">{localized(lang, u, "description")}</span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {t("bookings.capacity", { count: u.capacity })}
              </span>
              {u.total_units > 1 && (
                <span
                  className="mt-1 inline-block rounded-full bg-[hsl(var(--primary))]/10 px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--primary))]"
                  data-testid={`unit-count-${u.id}`}
                >
                  {t("bookings.roomsOfType", { count: u.total_units })}
                </span>
              )}
              <span className="mt-1 block tabular-nums text-sm">
                {rupees({ minor_units: u.price?.minor_units })} <span className="text-xs text-muted-foreground">
                  {u.unit_type === "room" ? t("bookings.perNight") : t("bookings.perDay")}
                </span>
              </span>
            </span>
          </button>
        ))}
        {units.length === 0 && <p className="text-muted-foreground">{t("common.noResults")}</p>}
      </div>

      {selected && (
        <div className="overflow-hidden rounded-[22px] border border-border bg-background">
          {/* Storefront S5 (gap review item 8): same dark-header-band pattern as the table/trial
              booking cards, for visual consistency — the handoff draws its own bespoke check-in/
              out bar above the hero for stay bookings, which is a bigger structural change this
              pass does not attempt; this keeps the existing room-then-dates flow but gives it the
              same header signature. */}
          <div className="bg-[hsl(var(--brand-deep))] px-6 py-5">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-white/15 text-white">
                <BedDouble size={17} />
              </span>
              <h2 className="font-heading text-lg font-bold text-white">{t("bookings.checkAvailability")}</h2>
              <span className="ml-auto rounded-full bg-[hsl(var(--accent))] px-3 py-1 text-xs font-bold text-[hsl(var(--accent-ink))]">
                {t("tables.freeInstant")}
              </span>
            </div>
          </div>
          <div className="space-y-4 p-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="check-in">{selected.unit_type === "room" ? t("bookings.checkIn") : t("bookings.fromDate")}</Label>
              <Input
                id="check-in"
                type="date"
                min={addDays(0)}
                data-testid="check-in-input"
                value={range.start_on}
                onChange={(e) => setRange({ ...range, start_on: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="check-out">{selected.unit_type === "room" ? t("bookings.checkOut") : t("bookings.toDate")}</Label>
              <Input
                id="check-out"
                type="date"
                min={range.start_on}
                data-testid="check-out-input"
                value={range.end_on}
                onChange={(e) => setRange({ ...range, end_on: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="guests">{t("bookings.guests")}</Label>
              <Input
                id="guests"
                type="number"
                min={1}
                data-testid="guests-input"
                value={form.guests}
                onChange={(e) => setForm({ ...form, guests: e.target.value })}
              />
            </div>
          </div>

          <Button variant="outline" className="rounded-full" data-testid="check-availability-btn" onClick={check}>
            {t("bookings.checkAvailability")}
          </Button>

          {availability && (
            <div className="rounded-xl border border-border p-4" data-testid="availability-result">
              <p className={availability.is_available ? "text-[hsl(var(--primary))]" : "text-destructive"}>
                {availability.is_available ? t("bookings.available") : t("bookings.notAvailable")}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {availability.nights === 1 ? t("bookings.night") : t("bookings.nights", { count: availability.nights })} ·{" "}
                {t("bookings.totalPrice")}{" "}
                <span className="tabular-nums text-foreground">{rupees({ minor_units: availability.total?.minor_units })}</span>
              </p>
            </div>
          )}

          {availability?.is_available && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="guest-name">{t("bookings.guestName")}</Label>
                  <Input
                    id="guest-name"
                    data-testid="guest-name-input"
                    value={form.guest_name}
                    onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="guest-phone">{t("bookings.guestPhone")}</Label>
                  <Input
                    id="guest-phone"
                    data-testid="guest-phone-input"
                    value={form.guest_phone}
                    onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                  />
                </div>
              </div>
              {/* UAT fix: `business` here is the public storefront shape, which never sends
                  `upi_id` (owner-only) — `accepts_upi` is the public boolean that carries this. */}
              {business.accepts_upi && (
                <div className="flex gap-2">
                  {[
                    ["cash", t("payment.cashMode")],
                    ["upi", t("payment.upiMode")],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      data-testid={`booking-payment-method-${value}`}
                      onClick={() => setForm({ ...form, payment_method: value })}
                      className={`rounded-full border px-4 py-1.5 text-sm ${
                        form.payment_method === value
                          ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <Button className="rounded-full" data-testid="create-booking-btn" disabled={busy} onClick={book}>
                {busy ? t("common.saving") : auth ? t("bookings.book") : t("bookings.signInToBook")}
              </Button>
              <p className="text-xs text-muted-foreground">
                {form.payment_method === "upi" ? t("payment.payAfterPlacing") : t("commerce.payAtDelivery")}
              </p>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
